import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Check, X, Trash2, Mail, Phone, Users, Calendar, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { InlineSpinner } from '../components/InlineSpinner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './ReservationsPage.module.css';

interface Reservation {
  _id: string;
  workshop: {
    _id: string;
    name: string;
    date: string;
    time: string;
  };
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  numberOfSeats: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  isPaid: boolean;
  createdAt: string;
  selectedKit?: { _id: string; name: string } | null;
  selectedFriendKit?: { _id: string; name: string } | null;
}

interface GroupedReservations {
  workshopId: string;
  workshopName: string;
  workshopDate: string;
  workshopTime: string;
  reservations: Reservation[];
  totalSeats: number;
  pendingCount: number;
  confirmedCount: number;
}

export function ReservationsPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [expandedWorkshops, setExpandedWorkshops] = useState<Set<string>>(new Set());
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const isBusy = (id: string, action: string) => busyAction === `${id}:${action}`;
  const isRowBusy = (id: string) => !!busyAction && busyAction.startsWith(`${id}:`);

  const fetchReservations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setReservations(data.reservations || []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [token]);

  const updateStatus = async (id: string, status: 'confirmed' | 'cancelled') => {
    setBusyAction(`${id}:${status === 'confirmed' ? 'confirm' : 'cancel'}`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to update status');
      await fetchReservations();
    } catch (error) {
      alert('Failed to update reservation status');
    } finally {
      setBusyAction(null);
    }
  };

  const togglePaid = async (id: string, currentIsPaid: boolean) => {
    setBusyAction(`${id}:paid`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isPaid: !currentIsPaid }),
      });

      if (!response.ok) throw new Error('Failed to update payment status');
      await fetchReservations();
    } catch (error) {
      alert('Failed to update payment status');
    } finally {
      setBusyAction(null);
    }
  };

  const deleteReservation = async (id: string) => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this reservation? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setBusyAction(`${id}:delete`);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete');
      await fetchReservations();
    } catch (error) {
      alert('Failed to delete reservation');
    } finally {
      setBusyAction(null);
    }
  };

  const filteredReservations = reservations.filter((r) => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  // Group reservations by workshop
  const groupedReservations = useMemo(() => {
    const groups: Record<string, GroupedReservations> = {};

    filteredReservations.forEach((reservation) => {
      const workshopId = reservation.workshop?._id || 'unknown';

      if (!groups[workshopId]) {
        groups[workshopId] = {
          workshopId,
          workshopName: reservation.workshop?.name || 'Unknown Workshop',
          workshopDate: reservation.workshop?.date || '',
          workshopTime: reservation.workshop?.time || '',
          reservations: [],
          totalSeats: 0,
          pendingCount: 0,
          confirmedCount: 0,
        };
      }

      groups[workshopId].reservations.push(reservation);
      groups[workshopId].totalSeats += reservation.numberOfSeats;
      if (reservation.status === 'pending') groups[workshopId].pendingCount++;
      if (reservation.status === 'confirmed') groups[workshopId].confirmedCount++;
    });

    // Sort by workshop date (most recent first)
    return Object.values(groups).sort((a, b) => {
      if (!a.workshopDate) return 1;
      if (!b.workshopDate) return -1;
      return new Date(b.workshopDate).getTime() - new Date(a.workshopDate).getTime();
    });
  }, [filteredReservations]);

  // Expand all workshops by default when data loads
  useEffect(() => {
    if (groupedReservations.length > 0 && expandedWorkshops.size === 0) {
      setExpandedWorkshops(new Set(groupedReservations.map(g => g.workshopId)));
    }
  }, [groupedReservations]);

  const toggleWorkshop = (workshopId: string) => {
    setExpandedWorkshops((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(workshopId)) {
        newSet.delete(workshopId);
      } else {
        newSet.add(workshopId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const badgeStyles: Record<string, React.CSSProperties> = {
      pending: { background: '#fef3c7', color: '#92400e' },
      confirmed: { background: '#d1fae5', color: '#065f46' },
      cancelled: { background: '#fee2e2', color: '#991b1b' },
    };
    return badgeStyles[status] || {};
  };

  const getPaidBadgeStyle = (isPaid: boolean): React.CSSProperties =>
    isPaid
      ? { background: '#d1fae5', color: '#065f46' }
      : { background: '#fee2e2', color: '#991b1b' };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Reservations</h1>
          <p>Manage workshop reservations</p>
        </div>
        <div className={styles.filters}>
          {['all', 'pending', 'confirmed', 'cancelled'].map((f) => (
            <button
              key={f}
              className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className={styles.loading}>Loading reservations...</div>
      ) : groupedReservations.length === 0 ? (
        <div className={styles.empty}>
          <p>No {filter !== 'all' ? filter : ''} reservations found.</p>
        </div>
      ) : (
        <div className={styles.workshopGroups}>
          {groupedReservations.map((group) => (
            <div key={group.workshopId} className={styles.workshopGroup}>
              <button
                className={styles.workshopHeader}
                onClick={() => toggleWorkshop(group.workshopId)}
              >
                <div className={styles.workshopHeaderLeft}>
                  {expandedWorkshops.has(group.workshopId) ? (
                    <ChevronDown size={22} />
                  ) : (
                    <ChevronRight size={22} />
                  )}
                  <div className={styles.workshopTitle}>
                    <h2>{group.workshopName}</h2>
                    <div className={styles.workshopMeta}>
                      <span>
                        <Calendar size={15} />
                        {group.workshopDate ? formatDate(group.workshopDate) : 'No date'}
                      </span>
                      <span>
                        <Clock size={15} />
                        {group.workshopTime || 'No time'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.workshopStats}>
                  <span className={styles.statTotal}>
                    <Users size={16} />
                    {group.totalSeats} seats
                  </span>
                  <span className={styles.statPending}>
                    {group.pendingCount} pending
                  </span>
                  <span className={styles.statConfirmed}>
                    {group.confirmedCount} confirmed
                  </span>
                </div>
              </button>

              {expandedWorkshops.has(group.workshopId) && (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Guest</th>
                        <th>Contact</th>
                        <th>Seats</th>
                        <th>Kit</th>
                        <th>Friend's Kit</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Booked On</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.reservations.map((reservation) => (
                        <tr key={reservation._id}>
                          <td>
                            <strong className={styles.guestName}>{reservation.guestName}</strong>
                          </td>
                          <td>
                            <div className={styles.contactInfo}>
                              <span>
                                <Mail size={14} /> {reservation.guestEmail}
                              </span>
                              <span>
                                <Phone size={14} /> {reservation.guestPhone}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={styles.seats}>
                              <Users size={16} />
                              {reservation.numberOfSeats}
                            </span>
                          </td>
                          <td className={styles.dateCell}>
                            {reservation.selectedKit ? reservation.selectedKit.name : '—'}
                          </td>
                          <td className={styles.dateCell}>
                            {reservation.selectedFriendKit ? reservation.selectedFriendKit.name : '—'}
                          </td>
                          <td>
                            <span
                              className={styles.statusBadge}
                              style={getStatusBadge(reservation.status)}
                            >
                              {reservation.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className={styles.statusBadge}
                              style={{ ...getPaidBadgeStyle(reservation.isPaid), border: 'none', cursor: 'pointer' }}
                              onClick={() => togglePaid(reservation._id, reservation.isPaid)}
                              disabled={isRowBusy(reservation._id)}
                              title="Toggle payment status"
                            >
                              {isBusy(reservation._id, 'paid') ? (
                                <InlineSpinner size={12} />
                              ) : reservation.isPaid ? (
                                'Paid'
                              ) : (
                                'Unpaid'
                              )}
                            </button>
                          </td>
                          <td className={styles.dateCell}>{formatDate(reservation.createdAt)}</td>
                          <td>
                            <div className={styles.actions}>
                              {reservation.status === 'pending' && (
                                <>
                                  <button
                                    className={styles.confirmBtn}
                                    onClick={() => updateStatus(reservation._id, 'confirmed')}
                                    disabled={isRowBusy(reservation._id)}
                                    title="Confirm"
                                  >
                                    {isBusy(reservation._id, 'confirm') ? <InlineSpinner size={16} /> : <Check size={16} />}
                                  </button>
                                  <button
                                    className={styles.cancelBtn}
                                    onClick={() => updateStatus(reservation._id, 'cancelled')}
                                    disabled={isRowBusy(reservation._id)}
                                    title="Cancel"
                                  >
                                    {isBusy(reservation._id, 'cancel') ? <InlineSpinner size={16} /> : <X size={16} />}
                                  </button>
                                </>
                              )}
                              <button
                                className={styles.deleteBtn}
                                onClick={() => deleteReservation(reservation._id)}
                                disabled={isRowBusy(reservation._id)}
                                title="Delete"
                              >
                                {isBusy(reservation._id, 'delete') ? <InlineSpinner size={16} /> : <Trash2 size={16} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
